import 'swiper/css';
import classNames from 'classnames';
import { graphql, useStaticQuery } from 'gatsby';
import React from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';

import rightArrow from '_images/right-arrow.svg';

import ArticleCard from './ArticleCard';
import ArticleCardVertical from './ArticleCardVertical';
import NavLeftButton from './NavLeftButton';
import NavRightButton from './NavRightButton';

const tabs = [
  {
    label: 'Latest',
    value: 'latest',
  },
  {
    label: 'Reviews',
    value: 'car-reviews',
  },
  {
    label: 'News',
    value: 'news',
  },
  {
    label: 'Guides',
    value: 'guides',
  },
  {
    label: 'Features',
    value: 'features',
  },
];

export default function OtherArticles() {
  const swiperRef = React.useRef();
  const [selectedTabIndex, setSelectedTabIndex] = React.useState(0);

  const data = useStaticQuery(graphql`
    fragment OtherArticlesDetails on SanityArticle {
      _id
      title
      excerpt
      slug {
        current
      }
      thumbnail {
        asset {
          title
          description
          label
          url
          width
          height
        }
      }
      category_v3 {
        slug {
          current
        }
      }
      tags_v3 {
        name
        key {
          current
        }
      }
    }
    query OtherArticles {
      latestArticles: allSanityArticle(
        sort: { publishDate: DESC }
        filter: { hidden: { eq: false } }
        limit: 6
      ) {
        edges {
          node {
            ...OtherArticlesDetails
          }
        }
      }

      latestReviewsArticles: allSanityArticle(
        sort: { publishDate: DESC }
        filter: {
          hidden: { eq: false }
          category_v3: { slug: { current: { eq: "car-reviews" } } }
        }
        limit: 6
      ) {
        edges {
          node {
            ...OtherArticlesDetails
          }
        }
      }

      latestNewsArticles: allSanityArticle(
        sort: { publishDate: DESC }
        filter: {
          hidden: { eq: false }
          category_v3: { slug: { current: { eq: "news" } } }
        }
        limit: 6
      ) {
        edges {
          node {
            ...OtherArticlesDetails
          }
        }
      }

      latestGuidesArticles: allSanityArticle(
        sort: { publishDate: DESC }
        filter: {
          hidden: { eq: false }
          category_v3: { slug: { current: { eq: "guides" } } }
        }
        limit: 6
      ) {
        edges {
          node {
            ...OtherArticlesDetails
          }
        }
      }

      latestFeaturesArticles: allSanityArticle(
        sort: { publishDate: DESC }
        filter: {
          hidden: { eq: false }
          category_v3: { slug: { current: { eq: "features" } } }
        }
        limit: 6
      ) {
        edges {
          node {
            ...OtherArticlesDetails
          }
        }
      }
    }
  `);

  const allData = React.useMemo(
    () => [
      data.latestArticles,
      data.latestReviewsArticles,
      data.latestNewsArticles,
      data.latestGuidesArticles,
      data.latestFeaturesArticles,
    ],
    [data],
  );

  const links = React.useMemo(
    () => [
      '/articles/',
      '/articles/car-reviews/',
      '/articles/news/',
      '/articles/guides/',
      '/articles/features/',
    ],
    [],
  );

  const slidesPerView = React.useMemo(() => {
    if (typeof window === 'undefined') {
      return 3;
    }
    return window.innerWidth > 1024 ? 3 : 2;
  }, []);

  return (
    <div className="mt-8">
      <div className="flex justify-between items-center">
        <div className="text-lg sm:text-2xl font-bold text-content-primary">
          Other Articles
        </div>
        <a
          href={links[selectedTabIndex]}
          className="w-[150px] justify-end text-sm sm:text-base flex items-center gap-x-2.5 items-center flex-shrink-0 font-medium text-os-blue"
        >
          Explore more
          <img src={rightArrow} alt="right arrow" className="w-3" />
        </a>
      </div>

      <div className="flex justify-stretch gap-x-2 sm:gap-x-4 mt-5 sm:mt-8 w-full sm:w-auto">
        {tabs.map((t, index) => (
          <button
            type="button"
            onClick={() => {
              setSelectedTabIndex(index);
            }}
            key={t.value}
            className={classNames(
              'flex justify-center items-center gap-x-2 text-content-primary border rounded-full py-1 px-2 sm:py-2 sm:px-4 w-full sm:w-auto',
              selectedTabIndex === index
                ? 'border-os-border'
                : 'border-transparent',
            )}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="hidden sm:block mt-4 relative">
        <NavLeftButton onClick={() => swiperRef.current?.swiper.slidePrev()} />
        <NavRightButton onClick={() => swiperRef.current?.swiper.slideNext()} />
        <Swiper
          slidesPerView={slidesPerView}
          spaceBetween={32}
          ref={swiperRef}
          loop
        >
          {allData[selectedTabIndex].edges.map((d) => (
            <SwiperSlide key={d.node._id}>
              <ArticleCardVertical article={d.node} />
            </SwiperSlide>
          ))}
        </Swiper>
      </div>
      <div className="flex flex-col gap-y-4 sm:hidden mt-3">
        {allData[selectedTabIndex].edges.slice(0, 3).map((d) => (
          <ArticleCard key={d.node._id} article={d.node} />
        ))}
      </div>
    </div>
  );
}
